import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  format,
  startOfWeek,
  addDays,
  addWeeks,
  isSameDay,
  startOfDay,
  isBefore,
} from 'date-fns';
import clsx from 'clsx';
import { Lock } from 'lucide-react';
import { serviceCallsAPI, usersAPI } from '@shared/apiClient';
import { getServiceCallListCustomerName } from '@shared/utils';
import { useDispatchWorkspace } from './DispatchContext';
import { useDragAssign } from '../../hooks/useDragAssign';
import { buildTechColorMap, technicianName, UNASSIGNED_COLOR } from './constants';

const WEEK_STARTS_ON = 0;
const PAST_WEEKS = 2;
const FUTURE_WEEKS = 12;
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Sunday is never scheduled, so give it a narrower column than the other six days.
const WEEK_GRID_COLS = 'grid-cols-[0.5fr_1fr_1fr_1fr_1fr_1fr_1fr]';

const toDateParam = (date) => format(startOfDay(date), 'yyyy-MM-dd');

const parseScheduledDate = (value) => {
  if (!value) return null;
  const [datePart] = String(value).split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const buildScrollWeeks = () => {
  const thisWeek = startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON });
  const firstWeek = addWeeks(thisWeek, -PAST_WEEKS);
  const totalWeeks = PAST_WEEKS + 1 + FUTURE_WEEKS;
  return Array.from({ length: totalWeeks }, (_, i) => {
    const weekStart = addWeeks(firstWeek, i);
    const days = Array.from({ length: 7 }, (_, d) => addDays(weekStart, d));
    return { weekStart, days, isAnchorWeek: i === PAST_WEEKS };
  });
};

const getScrollFetchRange = () => {
  const thisWeek = startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON });
  const start = addWeeks(thisWeek, -PAST_WEEKS);
  const end = addDays(addWeeks(thisWeek, FUTURE_WEEKS + 1), -1);
  return { start: toDateParam(start), end: toDateParam(end) };
};

const getDayCellClass = (day) => {
  const today = startOfDay(new Date());
  const d = startOfDay(day);
  if (isSameDay(d, today)) return 'bg-primary-50 ring-1 ring-inset ring-primary-500';
  if (isBefore(d, today)) return 'bg-surface-secondary';
  return 'bg-surface';
};

export default function CalendarView() {
  const scrollContainerRef = useRef(null);
  const anchorWeekRef = useRef(null);
  const knownTechIdsRef = useRef(new Set());
  const [laneMode, setLaneMode] = useState(false);
  // null until roster loads; then a Set of visible tech ids (default: all)
  const [selectedTechIds, setSelectedTechIds] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null);
  const [dropHighlightDay, setDropHighlightDay] = useState(null);

  const {
    draggedItem,
    clearDrag,
    setSelectedDetail,
  } = useDispatchWorkspace();
  const { assignBacklogToTech } = useDragAssign();

  const scrollWeeks = useMemo(() => buildScrollWeeks(), []);
  const range = useMemo(() => getScrollFetchRange(), []);

  // Only label a week with its month when that week is the first in the list, or
  // when it contains the 1st of a new month — this surfaces month context without
  // repeating a "Week of..." row on every single week.
  const weekMonthLabels = useMemo(() => (
    scrollWeeks.map(({ weekStart, days }, idx) => {
      if (idx === 0) return format(weekStart, 'MMMM yyyy');
      const monthStartDay = days.find((d) => d.getDate() === 1);
      return monthStartDay ? format(monthStartDay, 'MMMM yyyy') : null;
    })
  ), [scrollWeeks]);

  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  const technicians = useMemo(
    () => (techniciansData?.data?.users || []).filter((t) => t.isActive),
    [techniciansData]
  );

  // Default all selected; auto-include newcomers only while "all" remains selected.
  useEffect(() => {
    if (technicians.length === 0) return;
    const rosterIds = technicians.map((t) => t.id);
    const known = knownTechIdsRef.current;

    setSelectedTechIds((prev) => {
      if (prev === null) return new Set(rosterIds);

      const next = new Set(prev);
      let changed = false;
      const hadAllKnownSelected = [...known].every((id) => prev.has(id));

      rosterIds.forEach((id) => {
        if (!known.has(id) && hadAllKnownSelected) {
          next.add(id);
          changed = true;
        }
      });
      [...next].forEach((id) => {
        if (!rosterIds.includes(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    knownTechIdsRef.current = new Set(rosterIds);
  }, [technicians]);

  const techColorMap = useMemo(() => buildTechColorMap(technicians), [technicians]);

  const allTechIdsSelected = selectedTechIds !== null
    && technicians.length > 0
    && technicians.every((t) => selectedTechIds.has(t.id));

  const visibleTechnicians = useMemo(
    () => (selectedTechIds === null
      ? technicians
      : technicians.filter((t) => selectedTechIds.has(t.id))),
    [technicians, selectedTechIds]
  );

  const toggleTech = (techId) => {
    setSelectedTechIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(techId)) next.delete(techId);
      else next.add(techId);
      return next;
    });
  };

  const selectAllTechs = () => {
    setSelectedTechIds(new Set(technicians.map((t) => t.id)));
  };

  const calendarParams = {
    start: range.start,
    end: range.end,
  };

  const { data: calendarData, isLoading } = useQuery(
    ['calendarServiceCalls', calendarParams],
    () => serviceCallsAPI.getCalendarServiceCalls(calendarParams),
    { keepPreviousData: true, refetchInterval: 30000 }
  );

  const calls = calendarData?.data?.serviceCalls || [];

  const eventsByDay = useMemo(() => {
    const map = new Map();
    const showAll = selectedTechIds === null || allTechIdsSelected;
    calls.filter((c) => c.scheduledDate).forEach((call) => {
      if (!showAll) {
        if (!call.technicianId || !selectedTechIds.has(call.technicianId)) return;
      }
      const startDate = parseScheduledDate(call.scheduledDate);
      if (!startDate) return;
      const key = toDateParam(startDate);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(call);
    });
    return map;
  }, [calls, selectedTechIds, allTechIdsSelected]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const anchor = anchorWeekRef.current;
    if (container && anchor) {
      container.scrollTop = anchor.offsetTop - container.offsetTop;
    }
  }, []);

  // When dropping on a day (not a swimlane), auto-assign only if exactly one tech is visible
  const soleVisibleTechId = visibleTechnicians.length === 1 ? visibleTechnicians[0].id : null;

  const handleDropOnDay = useCallback((day, techIdOverride) => {
    if (!draggedItem) return;
    const item = draggedItem;
    const dropDate = toDateParam(startOfDay(day));
    clearDrag();
    setDropHighlightDay(null);

    const techId = techIdOverride || soleVisibleTechId;
    if (techId) {
      assignBacklogToTech.mutate({
        item,
        technicianId: techId,
        scheduledDate: dropDate,
      });
    } else {
      setPendingDrop({ item, scheduledDate: dropDate });
    }
  }, [draggedItem, soleVisibleTechId, assignBacklogToTech, clearDrag]);

  const renderEventChip = (call) => {
    const techId = call.technicianId;
    const bg = techId ? techColorMap[techId] || UNASSIGNED_COLOR : UNASSIGNED_COLOR;
    const overdue = call.scheduledDate && isBefore(parseScheduledDate(call.scheduledDate), startOfDay(new Date()))
      && !['completed', 'cancelled'].includes(call.status);

    return (
      <button
        key={call.id}
        type="button"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', call.id);
        }}
        onDragEnd={(e) => {
          e.preventDefault();
        }}
        onClick={() => setSelectedDetail({ type: 'serviceCall', serviceCall: call })}
        className={clsx(
          'w-full text-left px-1.5 py-0.5 rounded text-2xs text-white truncate inline-flex items-center gap-0.5',
          overdue && 'ring-1 ring-red-400 ring-offset-1'
        )}
        style={{ backgroundColor: bg }}
        title={getServiceCallListCustomerName(call)}
      >
        {call.scheduleLocked && (
          <Lock className="inline h-3 w-3 mr-0.5 flex-shrink-0 opacity-90" aria-label="Schedule locked" />
        )}
        {getServiceCallListCustomerName(call)}
      </button>
    );
  };

  const renderDayCell = (day, techId = null) => {
    const key = toDateParam(day);
    const dayEvents = (eventsByDay.get(key) || []).filter(
      (call) => !techId || call.technicianId === techId
    );
    const isHighlighted = dropHighlightDay === key;

    return (
      <div
        key={`${key}-${techId || 'all'}`}
        className={clsx(
          'min-h-[72px] border border-surface-border p-1 flex flex-col',
          getDayCellClass(day),
          isHighlighted && 'hub-drop-target'
        )}
        onDragOver={(e) => {
          if (!draggedItem) return;
          e.preventDefault();
          setDropHighlightDay(key);
        }}
        onDragLeave={() => setDropHighlightDay(null)}
        onDrop={(e) => {
          e.preventDefault();
          handleDropOnDay(day, techId);
        }}
      >
        <div className="text-2xs font-medium text-gray-600 mb-1">
          {format(day, day.getDate() === 1 ? 'MMM d' : 'd')}
        </div>
        <div className="space-y-0.5 flex-1 overflow-auto">
          {dayEvents.map(renderEventChip)}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div
        className="flex-shrink-0 px-3 py-2 border-b border-surface-border bg-surface flex items-center gap-2 flex-wrap"
        data-tour-id="calendar-tech-filter"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0 flex-1">
          <span className="text-2xs font-semibold text-gray-500 uppercase tracking-wide shrink-0">
            Technicians
          </span>
          {technicians.length === 0 ? (
            <span className="text-2xs text-gray-400">No active technicians</span>
          ) : (
            technicians.map((tech) => {
              const checked = selectedTechIds === null || selectedTechIds.has(tech.id);
              const color = techColorMap[tech.id];
              return (
                <label
                  key={tech.id}
                  className={clsx(
                    'inline-flex items-center gap-1.5 text-xs cursor-pointer select-none',
                    checked ? 'text-gray-800' : 'text-gray-400'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleTech(tech.id)}
                    className="rounded border-gray-300"
                    style={{ accentColor: color }}
                  />
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: color, opacity: checked ? 1 : 0.35 }}
                    aria-hidden
                  />
                  <span className="truncate max-w-[9rem]">{technicianName(tech)}</span>
                </label>
              );
            })
          )}
          {technicians.length > 0 && !allTechIdsSelected && (
            <button
              type="button"
              onClick={selectAllTechs}
              className="text-2xs text-primary-600 hover:text-primary-800 shrink-0"
            >
              Select all
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setLaneMode((v) => !v)}
          className={clsx(
            'btn-sm shrink-0',
            laneMode ? 'btn-primary' : 'btn-secondary'
          )}
        >
          {laneMode ? 'Swimlanes on' : 'Swimlanes off'}
        </button>
        <button
          type="button"
          onClick={() => {
            const container = scrollContainerRef.current;
            const anchor = anchorWeekRef.current;
            if (container && anchor) {
              container.scrollTo({ top: anchor.offsetTop - container.offsetTop, behavior: 'smooth' });
            }
          }}
          className="btn-sm btn-secondary shrink-0"
        >
          Today
        </button>
        {isLoading && <span className="text-2xs text-gray-400 shrink-0">Loading...</span>}
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div
          className={clsx(
            'sticky top-0 z-20 grid h-6 bg-surface-secondary border-b border-surface-border',
            WEEK_GRID_COLS
          )}
        >
          {DAY_LABELS.map((label) => (
            <div
              key={label}
              className="flex items-center justify-center text-2xs font-semibold text-gray-600 border-r border-surface-border last:border-r-0"
            >
              {label}
            </div>
          ))}
        </div>

        {!laneMode ? (
          scrollWeeks.map(({ weekStart, days, isAnchorWeek }, weekIdx) => (
            <div
              key={weekStart.toISOString()}
              ref={isAnchorWeek ? anchorWeekRef : null}
              className="border-b border-surface-border"
            >
              {weekMonthLabels[weekIdx] && (
                <div className="sticky top-6 z-10 bg-surface-secondary px-3 py-1 text-xs font-semibold text-gray-700 border-b border-surface-border">
                  {weekMonthLabels[weekIdx]}
                </div>
              )}
              <div className={clsx('grid', WEEK_GRID_COLS)}>
                {days.map((day) => (
                  <div key={day.toISOString()} className="border-r border-surface-border last:border-r-0">
                    {renderDayCell(day)}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : visibleTechnicians.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 text-center">
            Select at least one technician to show swimlanes.
          </div>
        ) : (
          visibleTechnicians.map((tech) => (
            <div key={tech.id} className="border-b border-surface-border">
              <div
                className="sticky top-6 z-10 px-3 py-1 text-xs font-semibold text-white"
                style={{ backgroundColor: techColorMap[tech.id] }}
              >
                {technicianName(tech)}
              </div>
              {scrollWeeks.map(({ weekStart, days }, weekIdx) => (
                <div key={`${tech.id}-${weekStart.toISOString()}`}>
                  {weekMonthLabels[weekIdx] && (
                    <div className="text-2xs px-3 py-0.5 bg-surface-secondary text-gray-500">
                      {weekMonthLabels[weekIdx]}
                    </div>
                  )}
                  <div className={clsx('grid', WEEK_GRID_COLS)}>
                    {days.map((day) => renderDayCell(day, tech.id))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {pendingDrop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40">
          <div className="bg-surface rounded-lg border border-surface-border p-4 w-full max-w-sm shadow-xl">
            <h3 className="text-sm font-semibold mb-3">Assign to technician</h3>
            <p className="text-xs text-gray-500 mb-3">
              Schedule for {format(parseScheduledDate(pendingDrop.scheduledDate), 'MMM d, yyyy')}
            </p>
            <div className="space-y-1 max-h-60 overflow-auto">
              {technicians.map((tech) => (
                <button
                  key={tech.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm rounded hover:bg-surface-hover"
                  onClick={() => {
                    assignBacklogToTech.mutate({
                      item: pendingDrop.item,
                      technicianId: tech.id,
                      scheduledDate: pendingDrop.scheduledDate,
                    });
                    setPendingDrop(null);
                  }}
                >
                  {technicianName(tech)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="btn-secondary w-full mt-3"
              onClick={() => setPendingDrop(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
